import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, X, Check, MoreHorizontal, ChevronDown, Filter } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../lib/queryClient';
import { updateAppBadge } from '../lib/notifications';

interface Notification {
  id: string;
  playerId: string;
  title: string;
  description: string;
  type: 'match' | 'club' | 'premium' | 'system' | 'achievement';
  isRead: boolean;
  createdAt: string;
  metadata?: {
    matchId?: string;
    clubId?: string;
    [key: string]: any;
  };
}

interface NotificationPanelProps {
  playerId: string | null;
  onNavigateToMatches?: () => void;
  onNavigateToProfile?: (section: string) => void;
  onNavigateToPlayers?: () => void;
  onClose?: () => void;
}

export default function NotificationPanel({ playerId, onNavigateToMatches, onNavigateToProfile, onNavigateToPlayers, onClose }: NotificationPanelProps) {
  const { t, i18n } = useTranslation();
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const queryClient = useQueryClient();

  // Fetch notifications from API
  const { data: notifications = [], isLoading, refetch } = useQuery<Notification[]>({
    queryKey: ["/api/notifications"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/notifications");
      return response.json();
    },
    enabled: !!playerId,
    refetchOnWindowFocus: true,
    staleTime: 30000,
  });

  // Mark notification as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await apiRequest("PATCH", `/api/notifications/${notificationId}/read`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("PATCH", "/api/notifications/mark-all-read");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
    },
  });

  // Filter notifications based on selected filter
  const filteredNotifications = notifications.filter((notification) => {
    if (selectedFilter === 'all') return true;
    if (selectedFilter === 'unread') return !notification.isRead;
    return notification.type === selectedFilter;
  });

  const unreadCount = notifications.filter(n => !n.isRead).length;

  // Update app badge when unread count changes
  React.useEffect(() => {
    if (playerId) {
      updateAppBadge(unreadCount).catch((error) => {
        console.error('[NotificationPanel] Failed to update badge:', error);
      });
    }
  }, [unreadCount, playerId]);

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      markAsReadMutation.mutate(notification.id);
    }

    if (notification.metadata?.matchId && onNavigateToMatches) {
      onNavigateToMatches();
    }

    if (notification.type === 'premium' && onNavigateToProfile) {
      onNavigateToProfile("premium-section");
    }

    // FFT ranking updated - navigate to players tab
    if (notification.metadata?.type === 'fft_ranking_updated' && onNavigateToPlayers) {
      onNavigateToPlayers();
      if (onClose) onClose();
    }
  };

  const markAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInSeconds < 60) return t('notifications.time.justNow');
    if (diffInMinutes < 60) return `${diffInMinutes}${t('notifications.time.m')}${t('notifications.time.ago')}`;
    if (diffInHours < 24) return `${diffInHours}${t('notifications.time.h')}${t('notifications.time.ago')}`;
    return `${diffInDays}${t('notifications.time.d')}${t('notifications.time.ago')}`;
  };

  // Helper to translate notification content
  const getNotificationContent = (notification: Notification) => {
    let title = notification.title;
    let description = notification.description;

    // Try to translate based on type and metadata
    if (notification.type === 'premium') {
      const type = notification.metadata?.notificationType;
      const premiumType = notification.metadata?.premiumType;
      if (type === 'activation') {
        title = t('notifications.types.premiumActivated');
        description = t('notifications.types.premiumActivatedDesc', { premiumType });
      } else if (type === 'expiry_warning') {
        title = t('notifications.types.premiumExpiring');
        description = t('notifications.types.premiumExpiringDesc', { premiumType, days: notification.metadata?.daysLeft });
      } else if (type === 'expired') {
        title = t('notifications.types.premiumExpired');
        description = t('notifications.types.premiumExpiredDesc', { premiumType });
      } else if (type === 'deactivation') {
        title = t('notifications.types.premiumDeactivated');
        description = t('notifications.types.premiumDeactivatedDesc', { premiumType });
      } else if (type === 'renewal_reminder') {
        const endDate = notification.metadata?.endDate ? new Date(notification.metadata?.endDate) : new Date();
        title = t('notifications.types.premiumRenewal');
        description = t('notifications.types.premiumRenewalDesc', {
          premiumType,
          date: endDate.toLocaleDateString(i18n.language)
        });
      }
    } else if (notification.type === 'match') {
      if (notification.title.includes('Invitation')) {
        title = t('notifications.types.matchInvitation');
        description = t('notifications.types.matchInvitationDesc', { name: notification.metadata?.creatorName || 'A player' });
      } else if (notification.title.includes('Confirmation')) {
        title = t('notifications.types.matchConfirmation');
        description = t('notifications.types.matchConfirmationDesc');
      } else if (notification.title.includes('Cancelled')) {
        title = t('notifications.types.matchCancelled');
        description = t('notifications.types.matchCancelledDesc');
      } else if (notification.title.includes('Club Match Validation') || notification.title.includes('Partner Match Validation')) {
        title = t('notifications.types.clubMatchValidation');
        description = t('notifications.types.clubMatchValidationDesc', { clubName: notification.metadata?.clubName || 'Club' });
      }
    } else if (notification.type === 'club') {
      if (notification.metadata?.type === 'club_join_accepted') {
        title = t('notifications.types.clubJoinAccepted');
        description = t('notifications.types.clubJoinAcceptedDesc', { clubName: notification.metadata?.clubName || 'a club' });
      } else if (notification.title.includes('Invitation')) {
        title = t('notifications.types.clubInvitation');
        description = t('notifications.types.clubInvitationDesc', { name: notification.metadata?.inviterName || 'Someone', clubName: notification.metadata?.clubName || 'a club' });
      }
    }

    return { title, description };
  };

  const filterOptions = [
    { value: 'all', label: t('notifications.filters.all') },
    { value: 'unread', label: t('notifications.filters.unread') },
    { value: 'match', label: t('notifications.filters.matches') },
    { value: 'club', label: t('notifications.filters.clubs') },
    { value: 'premium', label: t('notifications.filters.premium') },
    { value: 'system', label: t('notifications.filters.system') },
    { value: 'achievement', label: t('notifications.filters.achievements') },
  ];

  const selectedFilterLabel = filterOptions.find(opt => opt.value === selectedFilter)?.label || t('notifications.filters.all');

  return (
    <div className="h-full flex flex-col bg-card">
      {/* Header */}
      <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-card sticky top-0 z-10">
        <div className="flex items-center space-x-3">
          <Bell className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-xl font-semibold text-foreground">{t('notifications.title')}</h2>
          {unreadCount > 0 && (
            <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 font-medium">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              disabled={markAllAsReadMutation.isPending}
            >
              {markAllAsReadMutation.isPending ? t('notifications.marking') : t('notifications.markAllRead')}
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Filter */}
      <div className="px-6 py-3 border-b border-border bg-gray-50 dark:bg-muted">
        <div className="relative">
          <button
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="w-full flex items-center justify-between px-3 py-2 bg-white dark:bg-card border border-border rounded-lg text-sm text-muted-foreground hover:bg-gray-50 dark:hover:bg-muted"
          >
            <span className="flex items-center space-x-2">
              <Filter className="w-4 h-4" />
              <span>{selectedFilterLabel}</span>
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform ${isFilterOpen ? 'rotate-180' : ''}`} />
          </button>

          {isFilterOpen && (
            <div className="absolute top-full left-0 mt-1 w-full bg-card border border-border rounded-md shadow-lg z-10">
              {filterOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setSelectedFilter(option.value);
                    setIsFilterOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-muted ${selectedFilter === option.value ? 'bg-blue-50 text-blue-700' : 'text-muted-foreground'
                    }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Notifications List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">{t('common.loading')}</div>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <Bell className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              {selectedFilter === 'unread' ? t('notifications.noUnread') : t('notifications.noNotifications')}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredNotifications.map((notification) => {
              const content = getNotificationContent(notification);
              return (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`px-6 py-4 hover:bg-muted cursor-pointer transition-colors ${!notification.isRead ? 'bg-blue-50 dark:bg-blue-950/40' : ''
                    }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className={`text-sm font-semibold ${!notification.isRead ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {content.title}
                        </h3>
                        {!notification.isRead && (
                          <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></span>
                        )}
                      </div>
                      <p className={`text-sm mb-2 line-clamp-2 ${!notification.isRead ? 'text-foreground dark:text-foreground' : 'text-muted-foreground'}`}>{content.description}</p>
                      <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                        <span>{getRelativeTime(notification.createdAt)}</span>
                        {notification.metadata?.matchId && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onNavigateToMatches?.();
                            }}
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            {t('notifications.actions.viewMatch')}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      {!notification.isRead && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsReadMutation.mutate(notification.id);
                          }}
                          className="w-6 h-6 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                          title={t('notifications.markAllRead')}
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Refresh Button */}
      <div className="px-6 py-4 border-t border-border bg-gray-50 dark:bg-muted">
        <button
          onClick={() => refetch()}
          disabled={isLoading}
          className="w-full px-4 py-2 bg-padel-green text-white rounded-lg hover:bg-padel-green-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? t('notifications.refreshing') : t('notifications.refresh')}
        </button>
      </div>
    </div>
  );
}

